import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AccessDeniedCard from './AccessDeniedCard';
import { normalizeRole } from './AdminRoute';

interface CorporateAdminRouteProps {
  children: React.ReactNode;
}

/** The denial line this guard shows (feat-338 design/NOTES.md §1). */
export const CORPORATE_ADMIN_DENIED_MESSAGE =
  'This page is only available to Bespoke staff and corporate users.';

/**
 * feat-338 — Admin + Corporate, NOT store Manager.
 *
 * Neither existing guard fits: `StaffRoute` admits Manager (Corporate gets the same
 * access as Manager there), and `AdminRoute` excludes Corporate. The backend routes
 * behind pages using this guard are `require_corporate_manager_or_admin` with the
 * `manager` role rejected, so a Manager reaching the page would only see 403s. This
 * guard denies BEFORE the page mounts, with the shared AccessDeniedCard.
 */
const CorporateAdminRoute: React.FC<CorporateAdminRouteProps> = ({ children }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-coral mx-auto"></div>
          <p className="mt-4 text-inkMuted">Loading…</p>
        </div>
      </div>
    );
  }

  if (!user || !user.isAuthenticated) {
    return <Navigate to="/scorecard-login" replace />;
  }

  const role = normalizeRole(user.managementUser?.role);
  if (role !== 'Admin' && role !== 'Corporate') {
    return <AccessDeniedCard message={CORPORATE_ADMIN_DENIED_MESSAGE} />;
  }

  return <>{children}</>;
};

export default CorporateAdminRoute;
